<script setup>
import { ref, computed, onMounted, watch, nextTick } from 'vue'
import { useStore } from 'vuex'
import { Button } from '@/components/ui/button'
import Chart from 'chart.js/auto'
import {formatTime} from '@/utils/datetime'
import { ChevronDown, ChevronUp, Calendar } from 'lucide-vue-next'

const config = {
  timeline: {
    defaultHourSpan: 24,
    hourInMs: 60 * 60 * 1000,
    navStepHours: 24,
    bufferMinutes: 1,
    maxRangeHours: 48
  },
  slider: {
    minRangePercentage: 5,
  },
  colors: {
    defaultChartColor: 'rgba(200, 200, 200, 0.6)',
    disabledAlpha: '0.6',
    enabledAlpha: '0.9',
    borderAlpha: '1',
    pointRadius: 0.5,
    pointHoverRadius: 1,
    pointBorderWidth: 0,
    tension: 0.2
  }
}

const props = defineProps({
  dimensions: {
    type: Array,
    default: () => []
  }
})

const store = useStore()
const settings = computed(() => store.getters['user/getUserSettings'])
const emit = defineEmits(['update:range'])

const isCollapsed = ref(localStorage.getItem('timeline_collapsed') === 'true')

function toggleCollapse() {
  isCollapsed.value = !isCollapsed.value
  localStorage.setItem('timeline_collapsed', isCollapsed.value)

  if (!isCollapsed.value) {
    nextTick(() => {
      updateContainerWidth()
      createChart()
    })
  }
}

function isDateRange(item) {
  return item.startDate && item.endDate
}

const processedDimensions = computed(() => {
  try {
    const dates = store.getters['layers/getFilteredDates'] || []
    let allDimensions = Array.isArray(props.dimensions) ? [...props.dimensions] : []
    let processedStoreDates = []

    if (Array.isArray(dates)) {
      dates.forEach(date => {
        if (date && date.layers && Array.isArray(date.layers)) {
          processedStoreDates.push(...date.layers)
        } else {
          processedStoreDates.push(date)
        }
      })
      allDimensions = [...allDimensions, ...processedStoreDates]
    }

    return allDimensions.map(dimension => {
      try {
        if (!dimension || typeof dimension !== 'object') {
          return { id: 'unknown', sortedDates: [], chartColor: getChartColor('gray', 'unknown') }
        }

        if (dimension.dates && typeof dimension.dates === 'object' && !Array.isArray(dimension.dates) && Array.isArray(dimension.dates.dates)) {
          const sortedDates = dimension.dates.dates
            .map(dateItem => processSingleDate(dateItem))
            .filter(date => date && date.dateObj && !isNaN(date.dateObj.getTime()))
            .sort((a, b) => a.dateObj - b.dateObj)

          return {
            id: dimension.id || 'unknown',
            name: dimension.name || dimension.id || 'Unknown',
            color: dimension.color || 'blue',
            sortedDates,
            chartColor: getChartColor(dimension.color || 'blue', dimension.id || 'unknown')
          }
        }

        if (!dimension.dates || !Array.isArray(dimension.dates) || dimension.dates.length === 0) {
          return {
            ...dimension,
            id: dimension.id || 'unknown',
            name: dimension.name || dimension.id || 'Unknown',
            sortedDates: [],
            chartColor: getChartColor(dimension.color || 'blue', dimension.id || 'unknown')
          }
        }

        const sortedDates = [...dimension.dates]
          .map(dateItem => {
            if (isDateRange(dateItem)) {
              return processDateRange(dateItem)
            } else {
              return processSingleDate(dateItem)
            }
          })
          .filter(date => date !== null)
          .sort((a, b) => {
            const aDate = a.isRange ? a.startDateObj : a.dateObj
            const bDate = b.isRange ? b.startDateObj : b.dateObj
            return aDate - bDate
          })

        return {
          ...dimension,
          id: dimension.id || 'unknown',
          sortedDates,
          chartColor: getChartColor(dimension.color || 'blue', dimension.id || 'unknown')
        }
      } catch (err) {
        console.error('Error processing dimension:', err)
        return { id: 'unknown', sortedDates: [], chartColor: getChartColor('gray', 'unknown') }
      }
    }).filter(dim => dim !== null && dim.id)
  } catch (err) {
    console.error('Error in processedDimensions:', err)
    return []
  }
})

function processSingleDate(dateItem) {
  try {
    const dateStr = dateItem.date
    if (!dateStr) return null

    let dateObj
    if (dateStr.endsWith('Z')) {
      dateObj = new Date(dateStr)
    } else if (dateStr.includes('+') || dateStr.includes('-')) {
      dateObj = new Date(dateStr)
    } else {
      dateObj = new Date(dateStr + 'Z')
    }

    if (isNaN(dateObj.getTime())) return null

    return {
      ...dateItem,
      isRange: false,
      dateObj,
      count: dateItem.count || 1
    }
  } catch (err) {
    return null
  }
}

function processDateRange(dateItem) {
  try {
    const startDateStr = dateItem.startDate
    const endDateStr = dateItem.endDate
    if (!startDateStr || !endDateStr) return null

    let startDateObj, endDateObj
    if (startDateStr.endsWith('Z')) {
      startDateObj = new Date(startDateStr)
    } else if (startDateStr.includes('+') || startDateStr.includes('-')) {
      startDateObj = new Date(startDateStr)
    } else {
      startDateObj = new Date(startDateStr + 'Z')
    }

    if (endDateStr.endsWith('Z')) {
      endDateObj = new Date(endDateStr)
    } else if (endDateStr.includes('+') || endDateStr.includes('-')) {
      endDateObj = new Date(endDateStr)
    } else {
      endDateObj = new Date(endDateStr + 'Z')
    }

    if (isNaN(startDateObj.getTime()) || isNaN(endDateObj.getTime())) return null

    return {
      ...dateItem,
      isRange: true,
      startDateObj,
      endDateObj,
      count: 1
    }
  } catch (err) {
    return null
  }
}

function getChartColor(color, id) {
  try {
    if (color && color.startsWith('#')) {
      return `rgba(${hexToRgb(color)}, ${config.colors.enabledAlpha})`
    }

    const colorMap = {
      blue: `rgba(59, 130, 246, ${config.colors.enabledAlpha})`,
      red: `rgba(239, 68, 68, ${config.colors.enabledAlpha})`,
      green: `rgba(34, 197, 94, ${config.colors.enabledAlpha})`,
      yellow: `rgba(234, 179, 8, ${config.colors.enabledAlpha})`,
      purple: `rgba(168, 85, 247, ${config.colors.enabledAlpha})`,
      indigo: `rgba(79, 70, 229, ${config.colors.enabledAlpha})`,
      gray: `rgba(107, 114, 128, ${config.colors.enabledAlpha})`
    }

    if (!color || typeof color !== 'string') {
      return colorMap.gray
    }

    if (color === 'blue' && typeof id === 'string') {
      if (id === 'radar') return colorMap.blue
      if (id === 'features') return colorMap.green
    }

    return colorMap[color] || colorMap.blue
  } catch (error) {
    return `rgba(107, 114, 128, ${config.colors.enabledAlpha})`
  }
}

function hexToRgb(hex) {
  try {
    hex = hex.replace(/^#/, '')
    let r, g, b
    if (hex.length === 3) {
      r = parseInt(hex.charAt(0) + hex.charAt(0), 16)
      g = parseInt(hex.charAt(1) + hex.charAt(1), 16)
      b = parseInt(hex.charAt(2) + hex.charAt(2), 16)
    } else {
      r = parseInt(hex.substring(0, 2), 16)
      g = parseInt(hex.substring(2, 4), 16)
      b = parseInt(hex.substring(4, 6), 16)
    }
    return `${r}, ${g}, ${b}`
  } catch (error) {
    return '107, 114, 128'
  }
}

const allDates = computed(() => {
  const dateSet = new Set()
  processedDimensions.value.forEach(dimension => {
    dimension.sortedDates.forEach(dateItem => {
      if (dateItem.isRange) {
        dateSet.add(dateItem.startDateObj.getTime())
        dateSet.add(dateItem.endDateObj.getTime())
      } else {
        dateSet.add(dateItem.dateObj.getTime())
      }
    })
  })

  return Array.from(dateSet)
    .map(time => new Date(time))
    .sort((a, b) => a - b)
})

const hasTimeline = computed(() => {
  if (allDates.value.length > 0) return true
  return processedDimensions.value.some(dimension =>
    dimension.sortedDates && dimension.sortedDates.length > 0
  )
})

const timelineEndPoint = ref(new Date())

const timeRangeOptions = [
  { value: 'all', label: 'All dates' },
  { value: 'year', label: 'Last year' },
  { value: '6months', label: 'Last 6 months' },
  { value: 'month', label: 'Last month' },
  { value: 'week', label: 'Last week' },
  { value: '24h', label: 'Last 24h' }
]

const selectedTimeRangeOption = ref('all')

const filteredTimeRange = computed(() => {
  const now = new Date()
  let endDate = now
  let startDate

  if (allDates.value.length) {
    const allMin = new Date(Math.min(...allDates.value.map(d => d.getTime())))
    const allMax = new Date(Math.max(...allDates.value.map(d => d.getTime())))
    endDate = allMax > now ? allMax : now

    if (selectedTimeRangeOption.value === 'all') {
      startDate = allMin
      startDate.setHours(startDate.getHours() - 1)
      return [startDate, endDate]
    }
  }

  switch (selectedTimeRangeOption.value) {
    case 'year':
      startDate = new Date(endDate)
      startDate.setFullYear(startDate.getFullYear() - 1)
      break
    case '6months':
      startDate = new Date(endDate)
      startDate.setMonth(startDate.getMonth() - 6)
      break
    case 'month':
      startDate = new Date(endDate)
      startDate.setMonth(startDate.getMonth() - 1)
      break
    case 'week':
      startDate = new Date(endDate)
      startDate.setDate(startDate.getDate() - 7)
      break
    case '24h':
      startDate = new Date(endDate)
      startDate.setHours(startDate.getHours() - 24)
      break
    case 'all':
    default:
      startDate = new Date(endDate)
      startDate.setMonth(startDate.getMonth() - 1)
      break
  }

  return [startDate, endDate]
})

const availableTimeRange = computed(() => filteredTimeRange.value)

const current12HourRange = computed(() => {
  if (availableTimeRange.value[0] && availableTimeRange.value[1]) {
    const end = new Date(availableTimeRange.value[1])
    const start = new Date(end)
    start.setHours(start.getHours() - config.timeline.defaultHourSpan)
    return [start, end]
  }

  const end = timelineEndPoint.value
  const start = new Date(end)
  start.setHours(start.getHours() - config.timeline.defaultHourSpan)
  return [start, end]
})

const selectedRange = ref([new Date(), new Date()])
const chartCanvas = ref(null)
const chartInstance = ref(null)
const container = ref(null)
const containerWidth = ref(0)
const leftThumbPosition = ref(0)
const rightThumbPosition = ref(100)
const isDragging = ref(null)
const startX = ref(0)
const dragStartPositions = ref({ left: 0, right: 0 })
const chartData = ref({ labels: [], datasets: [] })
const hourLabels = ref([])
const chartGranularity = ref({ useMonthly: false, useDaily: false, useHourly: false, hourInterval: 1 })
const chartAreaBounds = ref({ left: 0, right: 0, width: 0 })
const previousTimeRange = ref({ start: null, end: null })

const datasetVisibility = ref(new Map())
const allDatasets = ref([])

const visibleDatasets = computed(() => {
  return allDatasets.value.filter(dataset =>
    datasetVisibility.value.get(dataset.label) !== false
  )
})

onMounted(() => {
  updateContainerWidth()
  window.addEventListener('resize', updateContainerWidth)

  selectedRange.value = [filteredTimeRange.value[0], filteredTimeRange.value[1]]
  leftThumbPosition.value = 0
  rightThumbPosition.value = 100

  emitRangeUpdate()

  nextTick(() => {
    createChart()
  })
})

function updateContainerWidth() {
  if (container.value) {
    containerWidth.value = container.value.offsetWidth
  }
  if (chartInstance.value && chartInstance.value.chartArea) {
    const area = chartInstance.value.chartArea
    chartAreaBounds.value = { left: area.left, right: area.right, width: area.right - area.left }
  }
}

watch(processedDimensions, () => {
  if (chartInstance.value) {
    chartInstance.value.destroy()
    chartInstance.value = null
  }

  const [start, end] = filteredTimeRange.value
  const startTime = start.getTime()
  const endTime = end.getTime()
  const boundariesChanged = previousTimeRange.value.start !== startTime || previousTimeRange.value.end !== endTime

  if (boundariesChanged) {
    selectedRange.value = [start, end]
    previousTimeRange.value = { start: startTime, end: endTime }
  }

  nextTick(() => {
    createChart()
  })
}, { deep: true })

watch(isCollapsed, (newValue) => {
  if (!newValue) {
    nextTick(() => {
      updateContainerWidth()
      if (chartInstance.value) {
        chartInstance.value.destroy()
        chartInstance.value = null
      }
      createChart()
    })
  }
})

watch(selectedRange, () => {
  try {
    updateSliderPositions()
    if (chartInstance.value) {
      chartInstance.value.update('none')
    }
  } catch (error) {
    console.error('Error in selectedRange watcher:', error)
  }
})

watch(timelineEndPoint, () => {
  if (chartInstance.value) {
    chartInstance.value.destroy()
    chartInstance.value = null
  }

  const [start, end] = filteredTimeRange.value
  selectedRange.value = [start, end]
  leftThumbPosition.value = 0
  rightThumbPosition.value = 100

  nextTick(() => {
    updateContainerWidth()
    createChart()

    nextTick(() => {
      leftThumbPosition.value = 0
      rightThumbPosition.value = 100
    })
  })
}, { deep: false })

function navigateTimeline(direction) {
  const newEndPoint = new Date(timelineEndPoint.value)

  if (direction === 'back') {
    newEndPoint.setHours(newEndPoint.getHours() - config.timeline.navStepHours)
  } else if (direction === 'forward') {
    const now = new Date()
    newEndPoint.setHours(newEndPoint.getHours() + config.timeline.navStepHours)
    if (newEndPoint > now) {
      newEndPoint.setTime(now.getTime())
    }
  }

  timelineEndPoint.value = newEndPoint
}

function updateSliderPositions() {
  if (!hourLabels.value.length) return

  const startDate = selectedRange.value[0]
  const endDate = selectedRange.value[1]

  const startPos = getPositionForDate(startDate)
  const endPos = getPositionForDate(endDate)

  leftThumbPosition.value = startPos
  rightThumbPosition.value = endPos
}

function getPositionForDate(date) {
  if (!hourLabels.value.length || !containerWidth.value) return 0

  const N = hourLabels.value.length
  const minTime = new Date(hourLabels.value[0]).getTime()
  const maxTime = new Date(hourLabels.value[N - 1]).getTime()
  const fullRange = maxTime - minTime
  const { left, width } = chartAreaBounds.value
  const totalWidth = containerWidth.value

  if (fullRange === 0 || width === 0 || totalWidth === 0) return 0

  const dateTime = date.getTime()
  const fracIndex = ((dateTime - minTime) / fullRange) * (N - 1)
  const pixelX = left + (fracIndex + 0.5) * width / N
  const position = (pixelX / totalWidth) * 100

  return Math.max(0, Math.min(100, position))
}

function getDateForPosition(position) {
  if (!hourLabels.value.length || !containerWidth.value) return new Date()

  const N = hourLabels.value.length
  const { left, width } = chartAreaBounds.value
  const totalWidth = containerWidth.value

  if (width === 0 || totalWidth === 0) return new Date(hourLabels.value[0])

  const pixelX = (position / 100) * totalWidth
  const fracIndex = (pixelX - left) / width * N - 0.5
  const index = Math.min(Math.max(0, Math.round(fracIndex)), N - 1)

  return new Date(hourLabels.value[index])
}

function generateFrequencyData() {
  const frequencyMap = new Map()
  const datasets = []

  const end = availableTimeRange.value[1]
  const start = availableTimeRange.value[0]

  let timeLabels = []

  const totalHours = Math.ceil((end.getTime() - start.getTime()) / config.timeline.hourInMs)
  const totalDays = Math.ceil(totalHours / 24)

  let hourInterval = 1
  let useHourly = false
  let useDaily = false
  let useMonthly = false

  if (selectedTimeRangeOption.value === '24h') {
    useHourly = true
    hourInterval = 1
  } else if (selectedTimeRangeOption.value === 'week') {
    useDaily = true
    hourInterval = 4
  } else if (selectedTimeRangeOption.value === 'month') {
    useDaily = true
    hourInterval = 6
  } else {
    useMonthly = true
    hourInterval = 24 * 30
  }

  if (useHourly) {
    if (totalHours > 336) {
      hourInterval = 4
    } else if (totalHours > 168) {
      hourInterval = 2
    }
  }

  chartGranularity.value = { useMonthly, useDaily, useHourly, hourInterval }

  if (useMonthly) {
    const currentDate = new Date(start)
    currentDate.setDate(1)
    currentDate.setHours(0, 0, 0, 0)

    while (currentDate <= end) {
      const label = currentDate.toISOString()
      timeLabels.push(label)
      currentDate.setMonth(currentDate.getMonth() + 1)
    }
  } else if (useDaily) {
    const startDate = new Date(start)

    if (startDate.getHours() >= 12) {
      startDate.setHours(12, 0, 0, 0)
    } else {
      startDate.setHours(0, 0, 0, 0)
    }

    while (startDate <= end) {
      const label = startDate.toISOString()
      timeLabels.push(label)
      startDate.setHours(startDate.getHours() + hourInterval)
    }
  } else {
    for (let i = 0; i <= totalHours; i += hourInterval) {
      const date = new Date(start.getTime() + i * config.timeline.hourInMs)
      date.setMinutes(0, 0, 0)
      const label = date.toISOString()
      timeLabels.push(label)
    }
  }

  hourLabels.value = [...timeLabels]

  timeLabels.forEach(label => {
    frequencyMap.set(label, {})
  })

  processedDimensions.value.forEach(dimension => {
    const dimensionId = dimension.id
    const dimensionName = dimension.name || dimensionId

    timeLabels.forEach(label => {
      if (!frequencyMap.get(label)[dimensionId]) {
        frequencyMap.get(label)[dimensionId] = 0
      }
    })

    dimension.sortedDates.forEach(dateItem => {
      if (dateItem.isRange) {
        const startTime = dateItem.startDateObj.getTime()
        const endTime = dateItem.endDateObj.getTime()

        for (let labelTime of timeLabels) {
          const bucketTime = new Date(labelTime)
          const bucketStart = new Date(bucketTime)
          bucketStart.setMinutes(0, 0, 0)
          const bucketEnd = new Date(bucketStart)
          bucketEnd.setHours(bucketEnd.getHours() + hourInterval)

          if ((startTime <= bucketEnd.getTime() && endTime >= bucketStart.getTime())) {
            frequencyMap.get(labelTime)[dimensionId] = (frequencyMap.get(labelTime)[dimensionId] || 0) + 1
          }
        }
      } else {
        const eventTime = dateItem.dateObj.getTime()
        const eventCount = dateItem.count || 1

        for (let labelTime of timeLabels) {
          const bucketTime = new Date(labelTime)
          const bucketStart = new Date(bucketTime)
          bucketStart.setMinutes(0, 0, 0)
          const bucketEnd = new Date(bucketStart)
          bucketEnd.setHours(bucketEnd.getHours() + hourInterval)

          if (eventTime >= bucketStart.getTime() && eventTime < bucketEnd.getTime()) {
            frequencyMap.get(labelTime)[dimensionId] = (frequencyMap.get(labelTime)[dimensionId] || 0) + eventCount
          }
        }
      }
    })

    const data = timeLabels.map(label => frequencyMap.get(label)[dimensionId] || 0)

    datasets.push({
      label: dimensionId,
      displayName: dimensionName,
      data: data,
      backgroundColor: dimension.chartColor,
      borderColor: dimension.chartColor.replace(config.colors.enabledAlpha, config.colors.borderAlpha),
      borderWidth: 1,
      borderRadius: 2,
      categoryPercentage: 0.8,
      barPercentage: 0.95,
    })
  })

  allDatasets.value = [...datasets]
  datasets.forEach(dataset => {
    if (!datasetVisibility.value.has(dataset.label)) {
      datasetVisibility.value.set(dataset.label, true)
    }
  })

  const visibleDatasetsArray = datasets.filter(dataset =>
    datasetVisibility.value.get(dataset.label) !== false
  )

  return {
    labels: timeLabels,
    datasets: visibleDatasetsArray
  }
}

function createChart() {
  if (!chartCanvas.value) return

  updateContainerWidth()

  const data = generateFrequencyData()

  chartData.value = {
    labels: [...data.labels],
    datasets: JSON.parse(JSON.stringify(data.datasets))
  }

  if (data.datasets.length === 0 && data.labels.length > 0) {
    data.datasets = [{
      data: Array(data.labels.length).fill(0),
      backgroundColor: Array(data.labels.length).fill(config.colors.defaultChartColor),
      borderColor: config.colors.defaultChartColor,
      borderWidth: 1
    }]
  }

  if (chartInstance.value) {
    chartInstance.value.destroy()
    chartInstance.value = null
  }

  const chartAreaPlugin = {
    id: 'chartAreaCapture',
    afterLayout(chart) {
      const area = chart.chartArea
      if (area) {
        chartAreaBounds.value = { left: area.left, right: area.right, width: area.right - area.left }
      }
    }
  }

  const dimOutsideRangePlugin = {
    id: 'dimOutsideRange',
    beforeDraw(chart) {
      if (!hourLabels.value.length || !selectedRange.value[0] || !selectedRange.value[1]) return
      const startTime = selectedRange.value[0].getTime()
      const endTime = selectedRange.value[1].getTime()

      chart.data.datasets.forEach((dataset, datasetIndex) => {
        const meta = chart.getDatasetMeta(datasetIndex)
        if (meta.hidden) return
        meta.data.forEach((bar, index) => {
          const bucketTime = new Date(hourLabels.value[index]).getTime()
          const isInRange = bucketTime >= startTime && bucketTime <= endTime
          bar.options.backgroundColor = isInRange
            ? dataset.backgroundColor
            : dataset.backgroundColor.replace(/[\d.]+\)$/, '0.2)')
        })
      })
    }
  }

  chartInstance.value = new Chart(chartCanvas.value, {
    type: 'bar',
    data: JSON.parse(JSON.stringify(data)),
    plugins: [chartAreaPlugin, dimOutsideRangePlugin],
    options: {
      responsive: true,
      maintainAspectRatio: false,
      onClick: (event, elements) => {
        if (!elements.length) return
        const index = elements[0].index
        const bucketStart = new Date(hourLabels.value[index])
        let bucketEnd

        if (chartGranularity.value.useMonthly) {
          bucketEnd = new Date(bucketStart)
          bucketEnd.setMonth(bucketEnd.getMonth() + 1)
        } else {
          bucketEnd = new Date(bucketStart)
          bucketEnd.setHours(bucketEnd.getHours() + chartGranularity.value.hourInterval)
        }

        const [rangeStart, rangeEnd] = filteredTimeRange.value
        if (bucketEnd > rangeEnd) bucketEnd = rangeEnd
        if (bucketStart < rangeStart) bucketStart.setTime(rangeStart.getTime())

        selectedRange.value = [bucketStart, bucketEnd]
        updateSliderPositions()
        emitRangeUpdate()
      },
      onHover: (event, elements) => {
        event.native.target.style.cursor = elements.length ? 'pointer' : 'default'
      },
      scales: {
        y: {
          display: false,
          beginAtZero: true,
          stacked: true
        },
        x: {
          display: true,
          stacked: true,
          grid: {
            display: true,
            tickLength: 8,
            z: 1,
            drawOnChartArea: true,
            drawTicks: true,
            color: function(context) {
              if (selectedTimeRangeOption.value === 'week' || selectedTimeRangeOption.value === 'month') {
                if (context.tick && context.tick.label) {
                  const tickIndex = context.tick.index
                  const date = new Date(data.labels[tickIndex])
                  if (date && !isNaN(date.getTime()) && date.getHours() === 0) {
                    return 'rgba(200, 200, 200, 0.3)'
                  }
                }
              }
              return 'transparent'
            }
          },
          ticks: {
            autoSkip: true,
            maxRotation: 0,
            maxTicksLimit: 31,
            callback: function(value, index, values) {
              if (!data.labels[index]) return ''

              const date = new Date(data.labels[index])
              if (isNaN(date.getTime())) return ''

              if (selectedTimeRangeOption.value === '24h') {
                if (date.getHours() % 3 === 0) {
                  return `${String(date.getHours()).padStart(2, '0')}:00`
                }
              } else if (selectedTimeRangeOption.value === 'week' || selectedTimeRangeOption.value === 'month') {
                const isFirst = index === 0
                const isLast = index === data.labels.length - 1

                if (date.getHours() === 0 || isFirst || isLast) {
                  return `${date.getDate()} ${date.toLocaleString('default', { month: 'short' })}`
                }

                if (selectedTimeRangeOption.value === 'month') {
                  if ((date.getDate() % 3 === 0 || date.getDate() === 1 || date.getDate() % 10 === 0) &&
                     (date.getHours() === 0 || date.getHours() === 12)) {
                    return date.getHours() === 0 ?
                      `${date.getDate()} ${date.toLocaleString('default', { month: 'short' })}` :
                      `${date.getDate()}`
                  }
                } else {
                  if (date.getHours() === 0 || date.getHours() === 12) {
                    return date.getHours() === 0 ?
                      `${date.getDate()} ${date.toLocaleString('default', { month: 'short' })}` :
                      `${date.getDate()}`
                  }
                }
              } else {
                if (date.getDate() === 1 && date.getHours() === 0) {
                  return date.toLocaleString('default', { month: 'short' })
                } else if (date.getHours() === 0 && (date.getDate() % 5 === 0 || index % Math.ceil(data.labels.length / 10) === 0)) {
                  return `${date.getDate()} ${date.toLocaleString('default', { month: 'short' })}`
                }
              }
              return ''
            },
          }
        },
      },
      elements: {
        bar: {
          borderWidth: 1,
          borderRadius: 2
        }
      },
      plugins: {
        tooltip: {
          mode: 'index',
          intersect: false,
          callbacks: {
            title: function(tooltipItems) {
              if (!tooltipItems || tooltipItems.length === 0) return ''
              const item = tooltipItems[0]
              if (!item || !item.label) return ''
              const date = new Date(item.label)
              return formatTime(date, settings.value.tz, 'D MMMM YYYY HH:mm')
            },
            label: function(context) {
              try {
                const displayName = context.dataset.displayName || context.dataset.label || 'Unknown'
                const count = context.raw || 0
                return `${displayName}: ${count} events`
              } catch (error) {
                return `${context.dataset.label}: ${context.raw}`
              }
            }
          }
        },
        legend: {
          display: false
        }
      },
      animation: {
        duration: 0
      }
    }
  })

  nextTick(() => {
    updateSliderPositions()
  })
}

function formatDateShort(date) {
  return `${date.toLocaleDateString()} ${String(date.getHours()).padStart(2, '0')}:00`
}

function startDrag(event, dragType) {
  event.preventDefault()

  isDragging.value = dragType
  startX.value = event.clientX || (event.touches && event.touches[0].clientX)

  if (dragType === 'range') {
    dragStartPositions.value = {
      left: leftThumbPosition.value,
      right: rightThumbPosition.value
    }
  }

  document.addEventListener('mousemove', onDrag)
  document.addEventListener('touchmove', onDrag, { passive: false })
  document.addEventListener('mouseup', stopDrag)
  document.addEventListener('touchend', stopDrag)
  document.addEventListener('touchcancel', stopDrag)
}

function onDrag(event) {
  if (!isDragging.value || !containerWidth.value) return

  event.preventDefault()

  const clientX = event.clientX || (event.touches && event.touches[0].clientX)
  const deltaX = clientX - startX.value
  const deltaPercent = (deltaX / containerWidth.value) * 100

  if (isDragging.value === 'start') {
    let newPos = Math.max(0, Math.min(rightThumbPosition.value - config.slider.minRangePercentage,
                                                  leftThumbPosition.value + deltaPercent))
    const snappedDate = getDateForPosition(newPos)
    newPos = getPositionForDate(snappedDate)
    leftThumbPosition.value = newPos
    selectedRange.value[0] = snappedDate
    startX.value = clientX
  } else if (isDragging.value === 'end') {
    let newPos = Math.min(100, Math.max(leftThumbPosition.value + config.slider.minRangePercentage,
                                                     rightThumbPosition.value + deltaPercent))
    const snappedDate = getDateForPosition(newPos)
    newPos = getPositionForDate(snappedDate)
    rightThumbPosition.value = newPos
    selectedRange.value[1] = snappedDate
    startX.value = clientX
  } else if (isDragging.value === 'range') {
    const rangeWidth = dragStartPositions.value.right - dragStartPositions.value.left

    let newLeftPos = dragStartPositions.value.left + deltaPercent
    let newRightPos = dragStartPositions.value.right + deltaPercent

    if (newLeftPos < 0) {
      newLeftPos = 0
      newRightPos = rangeWidth
    }

    if (newRightPos > 100) {
      newRightPos = 100
      newLeftPos = 100 - rangeWidth
    }

    leftThumbPosition.value = newLeftPos
    rightThumbPosition.value = newRightPos

    selectedRange.value[0] = getDateForPosition(leftThumbPosition.value)
    selectedRange.value[1] = getDateForPosition(rightThumbPosition.value)
  }

  emitRangeUpdate()
}

function stopDrag() {
  if (isDragging.value === 'range') {
    const snappedStartDate = getDateForPosition(leftThumbPosition.value)
    const snappedEndDate = getDateForPosition(rightThumbPosition.value)
    leftThumbPosition.value = getPositionForDate(snappedStartDate)
    rightThumbPosition.value = getPositionForDate(snappedEndDate)
    selectedRange.value = [snappedStartDate, snappedEndDate]
    emitRangeUpdate()
  }
  isDragging.value = null
  document.removeEventListener('mousemove', onDrag)
  document.removeEventListener('touchmove', onDrag)
  document.removeEventListener('mouseup', stopDrag)
  document.removeEventListener('touchend', stopDrag)
  document.removeEventListener('touchcancel', stopDrag)
}

function emitRangeUpdate() {
  const startDate = new Date(selectedRange.value[0])
  startDate.setSeconds(0)
  startDate.setMilliseconds(0)

  const endDate = new Date(selectedRange.value[1])
  endDate.setSeconds(59)
  endDate.setMilliseconds(999)

  const rangeArray = [
    startDate.toISOString(),
    endDate.toISOString()
  ]

  emit('update:range', rangeArray)
  store.commit('layers/SET_SELECTED_RANGE', rangeArray)
}

function resetTimeRange() {
  try {
    const [start, end] = filteredTimeRange.value

    selectedRange.value = [start, end]

    leftThumbPosition.value = 0
    rightThumbPosition.value = 100

    emitRangeUpdate()
  } catch (error) {
    console.error('Error in resetTimeRange:', error)
  }
}

function toggleDatasetVisibility(datasetLabel) {
  const currentVisibility = datasetVisibility.value.get(datasetLabel)
  datasetVisibility.value.set(datasetLabel, !currentVisibility)

  if (chartInstance.value) {
    chartInstance.value.destroy()
    chartInstance.value = null
  }
  nextTick(() => {
    createChart()
  })
}

watch(selectedTimeRangeOption, (newValue) => {
  if (chartInstance.value) {
    chartInstance.value.destroy()
    chartInstance.value = null
  }

  // Calculate the new range first, before any async operations
  let newStart, newEnd
  try {
    const [start, end] = filteredTimeRange.value
    newStart = start
    newEnd = end

    if (newValue === '24h') {
      const endTime = new Date(end)
      const startTime = new Date(endTime)
      startTime.setHours(startTime.getHours() - 24)
      newStart = startTime
      newEnd = endTime
    }
  } catch (error) {
    console.error('Error calculating time range:', error)
    return
  }

  // Update range and positions synchronously
  selectedRange.value = [newStart, newEnd]
  leftThumbPosition.value = 0
  rightThumbPosition.value = 100

  // Single emit before chart recreation - this is the only API trigger
  emitRangeUpdate()

  // Recreate chart after the range update
  nextTick(() => {
    createChart()
  })
}, { deep: false })
</script>

<template>
<div
  class="select-none bg-background shadow-lg rounded-lg"
  :class="isCollapsed ? 'inline-block cursor-pointer hover:bg-muted hover:ring-1 hover:ring-primary/20 transition-all p-0.5' : 'w-full p-1'"
  ref="container"
  @click="isCollapsed && toggleCollapse()"
  :title="isCollapsed ? 'Click to expand timeline' : ''"
>
  <div v-if="!hasTimeline" class="flex justify-center items-center py-6 text-muted-foreground">
    No dates for timeline
  </div>

  <div v-else :class="{'w-full': !isCollapsed}">
    <div
      :class="[
        isCollapsed ? 'inline-flex items-center px-1 py-1' : 'flex justify-between items-center mb-1'
      ]"
    >
    <div class="flex items-center justify-between">
      <div class="flex items-center space-x-1 pr-1">
        <Button
          size="sm"
          variant="secondary"
          class="shrink-0 p-1 h-auto"
          @click.stop="toggleCollapse"
          :title="isCollapsed ? 'Expand timeline' : 'Collapse timeline'"
        >
          <ChevronUp v-if="isCollapsed" class="h-4 w-4 text-primary" />
          <ChevronDown v-else class="h-4 w-4" />
          <span class="text-sm ml-2 pr-1" v-if="!isCollapsed">Hide</span>
        </Button>
      </div>
    </div>

      <div v-if="!isCollapsed" class="flex items-center">
        <span class="text-muted-foreground text-sm">
          {{ formatTime(selectedRange[0], settings.tz, 'D MMMM YYYY HH:mm') }} -
          {{ formatTime(selectedRange[1], settings.tz, 'D MMMM YYYY HH:mm') }}
        </span>
        <Button
        v-if="selectedRange[0].getTime() !== filteredTimeRange[0].getTime() ||
                selectedRange[1].getTime() !== filteredTimeRange[1].getTime()"
          class="ml-2"
          variant="secondary"
          size="sm"
          @click="resetTimeRange"
          >
          Reset
        </Button>
        <div class="flex items-center ml-3" v-if="!isCollapsed">
          <select
            v-model="selectedTimeRangeOption"
            class="text-sm rounded-md bg-background focus:outline-none focus:ring-2 focus:ring-primary"
          >
            <option v-for="option in timeRangeOptions" :key="option.value" :value="option.value">
              {{ option.label }}
            </option>
          </select>
        </div>
      </div>

      <div v-if="isCollapsed" class="text-muted-foreground truncate ml-1 mr-1">
        {{ formatTime(selectedRange[0], settings.tz, 'D MMM HH:mm') }} -
        {{ formatTime(selectedRange[1], settings.tz, 'D MMM HH:mm') }}
      </div>
    </div>

    <div v-if="!isCollapsed && allDatasets.length > 1" class="flex flex-wrap gap-2">
        <Button
          v-for="dataset in allDatasets"
          :key="dataset.label"
          size="sm"
          variant="ghost"
          class="py-0.5 px-1 h-auto"
          :class="[
            datasetVisibility.get(dataset.label) !== false
              ? 'border-border bg-background'
              : 'border-border bg-muted opacity-60'
          ]"
          @click="toggleDatasetVisibility(dataset.label)"
        >
          <div
            class="w-2.5 h-2.5 rounded-full mr-1 flex-shrink-0"
            :style="{ backgroundColor: dataset.backgroundColor }"
          ></div>
          <span class="truncate">{{ dataset.displayName || dataset.label }}</span>
        </Button>
      </div>

    <div v-if="!isCollapsed" class="w-full pr-1">
      <div class="h-28 relative">
        <canvas ref="chartCanvas"></canvas>
      </div>

      <div class="relative h-5 mt-1 rounded">
        <div class="absolute top-0 left-0 right-0 bottom-0 pointer-events-none">
          <div
            class="absolute top-0 bottom-0 bg-muted-foreground opacity-30 pointer-events-none"
            :style="{
              left: '0',
              width: `${leftThumbPosition}%`,
            }">
          </div>

          <div
            class="absolute top-0 bottom-0 bg-muted-foreground opacity-30 pointer-events-none"
            :style="{
              left: `${rightThumbPosition}%`,
              right: '0',
            }">
          </div>
        </div>

        <div class="absolute top-0 left-0 right-0 bottom-0">
          <div
            class="absolute top-0 bottom-0 cursor-move bg-primary/20 rounded z-10"
            :style="{
              left: `${leftThumbPosition}%`,
              width: `${rightThumbPosition - leftThumbPosition}%`,
            }"
            @mousedown="startDrag($event, 'range')"
            @touchstart="startDrag($event, 'range')"
          ></div>

          <div
            class="w-2 h-7 -mt-2 rounded bg-primary absolute cursor-ew-resize z-20"
            :style="{
              left: `${leftThumbPosition}%`,
            }"
            @mousedown="startDrag($event, 'start')"
            @touchstart="startDrag($event, 'start')"
          >
          </div>

          <div
            class="w-2 h-7 -mt-2 -ml-2 rounded bg-primary absolute cursor-ew-resize z-20"
            :style="{
              left: `${rightThumbPosition}%`,
            }"
            @mousedown="startDrag($event, 'end')"
            @touchstart="startDrag($event, 'end')"
          >
          </div>
        </div>
      </div>
    </div>
  </div>
</div>
</template>
